import React, { useState } from 'react';
import { US_STATES } from '../USStates';
import { CompanyData, Product } from '../../types';
import { Check, Plus, Trash2, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import TermsOfServiceModal from '../TermsOfServiceModal';
import PhoneVerification from '../PhoneVerification';

interface OrganisationFormFieldsProps {
  formData: CompanyData;
  formErrors: {
    pocName: boolean;
    pocEmail: boolean;
    pocPhone?: boolean;
    name: boolean;
    address: boolean;
    stateOfIncorporation: boolean;
    ein: boolean;
    einDuplicate?: boolean;
    revenueLast12Months?: boolean;
    products: boolean;
    terms?: boolean;
  };
  einError?: string;
  isValidatingEin?: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleProductChange: (id: string, field: keyof Product, value: string) => void;
  addProduct: () => void;
  removeProduct: (id: string) => void;
  onTermsCheckedChange: (checked: boolean) => void;
  onPhoneChange?: (phone: string) => void;
  onPhoneVerificationComplete?: (verified: boolean) => void;
  disableEin?: boolean;
  showTermsModal: boolean;
  setShowTermsModal: React.Dispatch<React.SetStateAction<boolean>>;
  isAdminEdit?: boolean; // Add this prop
  /**
   * 'adminCreate' is the Admin › Add Merchant layout (FIG-319 / REQ-034):
   * Company Information first, then Point of Contact (name and email only).
   * No phone or Terms of Service: the contact adds both at first sign-in.
   */
  layout?: 'default' | 'adminCreate';
  /**
   * FIG-345 · UC-228: an existing merchant's Point of Contact is its sign-in
   * email and changes only in Settings › Merchant Contacts, so it is read-only here.
   */
  lockPointOfContact?: boolean;
}

const OrganisationFormFields: React.FC<OrganisationFormFieldsProps> = ({
  formData,
  formErrors,
  einError,
  isValidatingEin,
  handleInputChange,
  handleProductChange,
  addProduct,
  removeProduct,
  onTermsCheckedChange,
  onPhoneChange,
  onPhoneVerificationComplete,
  disableEin,
  showTermsModal,
  setShowTermsModal,
  isAdminEdit = false, // Default to false
  layout = 'default',
  lockPointOfContact = false
}) => {
  const isAdminCreate = layout === 'adminCreate';
  const [revenueInputFocused, setRevenueInputFocused] = useState(false);
  const [revenueRawValue, setRevenueRawValue] = useState('');

  const openTermsModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handleRevenueFocus = () => {
    setRevenueInputFocused(true);
    // Set raw value from formatted value
    const rawValue = formData.revenueLast12Months?.toString() || '';
    setRevenueRawValue(rawValue);
  };

  const handleRevenueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setRevenueInputFocused(false);
    // Create synthetic event with raw value to ensure proper parsing
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'revenueLast12Months',
        value: revenueRawValue
      }
    };
    handleInputChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  const handleRevenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    const sanitized = value.replace(/[^0-9.]/g, '');
    setRevenueRawValue(sanitized);

    // Create a synthetic event with the sanitized value for the parent handler
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: sanitized
      }
    };
    handleInputChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  const pocNameAndEmail = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="form-group">
        <label htmlFor="pocName" className="form-label">
          POC Name <span className="text-brand-secondary">*</span>
        </label>
        <input
          type="text"
          id="pocName"
          name="pocName"
          value={formData.pocName}
          onChange={handleInputChange}
          readOnly={lockPointOfContact}
          aria-readonly={lockPointOfContact || undefined}
          className={`form-input ${formErrors.pocName ? 'border-brand-secondary' : ''} ${lockPointOfContact ? 'bg-gray-50 text-gray-600' : ''}`}
          placeholder="Enter point of contact name"
        />
        {formErrors.pocName && (
          <p className="text-xs text-brand-secondary mt-1">POC Name is required</p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="pocEmail" className="form-label">
          POC Email <span className="text-brand-secondary">*</span>
        </label>
        <input
          type="email"
          id="pocEmail"
          name="pocEmail"
          value={formData.pocEmail}
          onChange={handleInputChange}
          readOnly={lockPointOfContact}
          aria-readonly={lockPointOfContact || undefined}
          className={`form-input ${formErrors.pocEmail ? 'border-brand-secondary' : ''} ${lockPointOfContact ? 'bg-gray-50 text-gray-600' : ''}`}
          placeholder="Enter point of contact email"
        />
        {formErrors.pocEmail && (
          <p className="text-xs text-brand-secondary mt-1">Valid email is required</p>
        )}
      </div>
      {lockPointOfContact && (
        <p className="text-xs text-gray-600 md:col-span-2">
          The Point of Contact is the merchant's sign-in email. Change it in Settings › Merchant Contacts.
        </p>
      )}
    </div>
  );

  const pocPhone = (
    <div className="form-group">
      <label className="form-label">
        POC Phone Number {!isAdminEdit && <span className="text-brand-secondary">*</span>}
      </label>
      {isAdminEdit ? (
        // Simple input for admin edits - no verification required
        <input
          type="tel"
          value={formData.pocPhone || ''}
          onChange={(e) => onPhoneChange?.(e.target.value)}
          readOnly={lockPointOfContact}
          aria-readonly={lockPointOfContact || undefined}
          aria-label="POC Phone Number"
          className={`form-input ${lockPointOfContact ? 'bg-gray-50 text-gray-600' : ''}`}
          placeholder="+1234567890"
        />
      ) : (
        // Full phone verification component for regular users
        <PhoneVerification
          phoneNumber={formData.pocPhone || ''}
          onPhoneChange={onPhoneChange || (() => { })}
          onVerificationComplete={onPhoneVerificationComplete || (() => { })}
          isVerified={formData.pocPhoneVerified || false}
          error={formErrors.pocPhone ? 'Phone number verification is required' : undefined}
        />
      )}
      {isAdminEdit && (
        <p className="text-xs text-gray-600 mt-1">
          Phone verification is not required for admin edits.
        </p>
      )}
    </div>
  );

  const companyFields = (
    <>
    <div className="form-group">
      <label htmlFor="name" className="form-label">
        Company Name <span className="text-brand-secondary">*</span>
      </label>
      <input
        type="text"
        id="name"
        name="name"
        value={formData.name}
        onChange={handleInputChange}
        className={`form-input ${formErrors.name ? 'border-brand-secondary' : ''}`}
        placeholder="Enter company name"
      />
      {formErrors.name && (
        <p className="text-xs text-brand-secondary mt-1">Company Name is required</p>
      )}
    </div>

    <div className="form-group">
      <label htmlFor="name_dbas" className="form-label">
        {isAdminCreate ? 'DBAs (doing business as)' : 'Company Name DBAs'}
      </label>
      <input
        type="text"
        id="name_dbas"
        name="name_dbas"
        value={formData.name_dbas || ''}
        onChange={handleInputChange}
        className="form-input"
        placeholder="Enter company 'doing business as' names (if any)"
      />
    </div>

    <div className="form-group">
      <label htmlFor="address" className="form-label">
        Company Address <span className="text-brand-secondary">*</span>
      </label>
      <input
        type="text"
        id="address"
        name="address"
        value={formData.address || ''}
        onChange={handleInputChange}
        className={`form-input ${formErrors.address ? 'border-brand-secondary' : ''}`}
        placeholder="Enter company address"
      />
      {formErrors.address && (
        <p className="text-xs text-brand-secondary mt-1">Company Address is required</p>
      )}
    </div>
    </>
  );

  const revenueField = (
    <div className="form-group">
      <label htmlFor="revenueLast12Months" className="form-label">
        {isAdminCreate ? 'Revenue (last 12 months, as stated by merchant)' : 'Revenue (last 12 months)'}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
        <input
          type="text"
          id="revenueLast12Months"
          name="revenueLast12Months"
          value={
            revenueInputFocused
              ? revenueRawValue
              : formData.revenueLast12Months
                ? formData.revenueLast12Months.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : ''
          }
          onChange={handleRevenueChange}
          onFocus={handleRevenueFocus}
          onBlur={handleRevenueBlur}
          className={`form-input !pl-10 ${formErrors.revenueLast12Months ? 'border-brand-secondary' : ''}`}
          placeholder="0.00"
        />
      </div>
      {formErrors.revenueLast12Months && (
        <p className="text-xs text-brand-secondary mt-1">Enter an amount such as 250000 or 250000.50</p>
      )}
    </div>
  );

  const stateAndEin = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="form-group">
        <label htmlFor="stateOfIncorporation" className="form-label">
          US State of Incorporation <span className="text-brand-secondary">*</span>
        </label>
        <select
          id="stateOfIncorporation"
          name="stateOfIncorporation"
          value={formData.stateOfIncorporation || ''}
          onChange={handleInputChange}
          className={`form-select ${formErrors.stateOfIncorporation ? 'border-brand-secondary' : ''}`}
        >
          <option value="">Select a State</option>
          {US_STATES.map(state => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
        {formErrors.stateOfIncorporation && (
          <p className="text-xs text-brand-secondary mt-1">State of Incorporation is required</p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="ein" className="form-label">
          EIN <span className="text-brand-secondary">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            id="ein"
            name="ein"
            value={formData.ein}
            onChange={handleInputChange}
            className={`form-input ${(formErrors.ein || formErrors.einDuplicate) ? 'border-brand-secondary' : ''} ${isValidatingEin ? 'pr-8' : ''}`}
            placeholder="XX-XXXXXXX"
            disabled={formData.ein && disableEin}
          />
          {isValidatingEin && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <Loader2 size={16} className="animate-spin text-gray-400" />
            </div>
          )}
        </div>
        {formErrors.ein && !formErrors.einDuplicate && (
          <p className="text-xs text-brand-secondary mt-1">Valid EIN format required (XX-XXXXXXX)</p>
        )}
        {formErrors.einDuplicate && einError && (
          <p className="text-xs text-brand-secondary mt-1">{einError}</p>
        )}
      </div>
    </div>
  );

  if (isAdminCreate) {
    return (
      <>
        <div className="form-section">
          <h2 className="form-header">Company Information</h2>
          {companyFields}
          {stateAndEin}
          {revenueField}
        </div>
        <div className="form-section mt-6">
          <h2 className="form-header">Point of Contact</h2>
          {pocNameAndEmail}
          <p className="mt-2 text-xs text-gray-600">
            The contact adds and verifies their phone number, and accepts the Terms of Service, when they first sign in.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="form-section">
        <h2 className="form-header">Company Information</h2>

        {pocNameAndEmail}

        {pocPhone}

        {companyFields}

        {revenueField}

        {stateAndEin}

        {/* <div className="form-section">
          <div className="flex justify-between items-center mb-4">
            <h2 className="form-header mb-0">Products</h2>
            <button
              type="button"
              onClick={addProduct}
              className="flex items-center text-brand-primary hover:text-brand-primary/80"
            >
              <Plus size={16} className="mr-1" /> Add Product
            </button>
          </div>
          
          {formErrors.products && (
            <p className="text-xs text-brand-secondary mt-1 mb-4">
              At least one product with all fields completed is required
            </p>
          )}
          
          {formData.products.map((product, index) => (
            <div key={product.id} className="p-4 border rounded-md mb-4 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-brand-basic">Product {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeProduct(product.id || '')}
                  className="text-gray-500 hover:text-brand-secondary"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">
                    Product Name <span className="text-brand-secondary">*</span>
                  </label>
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => handleProductChange(product.id || '', 'name', e.target.value)}
                    className="form-input"
                    placeholder="Enter product name"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Product Category <span className="text-brand-secondary">*</span>
                  </label>
                  <input
                    type="text"
                    value={product.category || ''}
                    onChange={(e) => handleProductChange(product.id || '', 'category', e.target.value)}
                    className="form-input"
                    placeholder="Enter product category"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Product Description <span className="text-brand-secondary">*</span>
                </label>
                <textarea
                  value={product.description}
                  onChange={(e) => handleProductChange(product.id || '', 'description', e.target.value)}
                  className="form-input h-24"
                  placeholder="Enter product description"
                />
              </div>
            </div>
          ))}
        </div> */}
      </div>

      <div className="mt-6 mb-6">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="terms"
            name="terms"
            checked={formData.terms}
            onCheckedChange={onTermsCheckedChange}
            className="mt-1"
            disabled={formData.terms}
          />
          <div className="grid gap-1.5 leading-none flex-grow">
            <Label style={{ paddingTop: "5px" }} htmlFor="terms" className="text-sm font-medium leading-none flex">
              By proceeding, you accept our{' '}
              <a href="#" onClick={openTermsModal} className="text-brand-primary underline hover:text-brand-primary/90 ml-1">
                Terms of Service and Privacy Policy
              </a>
            </Label>
          </div>
        </div>
      </div>

      <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </>
  );
};

export default OrganisationFormFields;
